import type {
  ChatAdapter,
  ListParams,
  MessagePayload,
  SessionConfig,
  SessionList,
  SessionPatch,
  SessionWithMessages,
} from "@anter/ai-chat-sdk/headless";

/**
 * ChatAdapter for the anter.ai landing page.
 *
 * The chat stream is powered by the Anter agent-runner API:
 *   POST /v1/external/agent-runner/agents/:agentId/run-stream
 *
 * In production this request is forwarded by the Cloudflare Worker (_worker.js),
 * which injects x-project-id and x-api-key from Cloudflare env vars so those
 * secrets are never shipped to the browser.
 *
 * During local dev the Vite dev server has no worker, so run through
 * `wrangler pages dev` when you need worker-injected auth headers.
 *
 * Session management (create/list/load/update/delete) is intentionally
 * stubbed out — the agent-runner API is stateless and the widget operates
 * in ephemeral (single-session) mode.
 */
export class LandingChatAdapter implements ChatAdapter {
  constructor(private readonly agentId: string) {}

  /**
   * The proxy path that maps to the agent-runner run-stream endpoint.
   * The CF worker strips /api and forwards to:
   *   https://api.anter.ai/v1/external/agent-runner/agents/:agentId/run-stream
   */
  private get streamPath(): string {
    return `/api/v1/external/agent-runner/agents/${this.agentId}/run-stream`;
  }

  // ─── Streaming ────────────────────────────────────────────────────────────

  async sendMessage(payload: MessagePayload): Promise<ReadableStream<Uint8Array>> {
    const res = await fetch(this.streamPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message: payload.message,
        organizationId: payload.organizationId,
        contextVariables: {
          ...(payload.contextVariables ?? {}),
          userId: `chat-widget-${payload.sessionId}`,
          sessionId: payload.sessionId,
        },
      }),
    });
    if (!res.ok) throw new Error(`sendMessage failed: ${res.status}`);
    if (!res.body) throw new Error("sendMessage: missing response body");
    return res.body;
  }

  // ─── Session stubs (agent-runner is stateless) ────────────────────────────

  async createSession(_config: SessionConfig): Promise<string> {
    // Return a stable ephemeral session ID — no backend call needed.
    return `local-${Date.now()}`;
  }

  async loadSession(sessionId: string): Promise<SessionWithMessages> {
    return { sessionId, title: "", updatedAt: new Date().toISOString(), messages: [] };
  }

  async listSessions(_params?: ListParams): Promise<SessionList> {
    return { sessions: [], total: 0, page: 0 };
  }

  async updateSession(_sessionId: string, _patch: SessionPatch): Promise<void> {
    // no-op
  }

  async deleteSession(_sessionId: string): Promise<void> {
    // no-op
  }
}
